
import Vue from 'vue'
import ListParagon from '../ListParagon.vue';
import { state } from '@/plugins/state';
import { EventBus } from '@/plugins/eventBus';
import { SnackBarData } from '../Snackbar.vue';
import axios, { AxiosError, AxiosResponse } from 'axios';

interface Space {
  id: number
  name: string
}

export default Vue.extend({
  name: 'CompanyList',
  components: {
    ListParagon
  },
  props: {
    deleted: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      view: "list",
      confirmationDialog: false,
      loading: false,
      action: 'archive' as 'archive' | 'reactivate',
      headers: [
        {
            text: this.$vuetify.lang.t("$vuetify.reference"),
            sortable: true,
            sortText: this.$vuetify.lang.t("$vuetify.reference"),
            value: 'id',
            width: '5%',
        },
        {
            text: this.$vuetify.lang.t("$vuetify.name"),
            sortable: true,
            value: 'name',
            width: '35%',
        },
        {
            text: this.$vuetify.lang.t("$vuetify.postal_code"),
            sortable: true,
            value: "postal_code",
            width: '10%',
        },
        {
            text: this.$vuetify.lang.t("$vuetify.city"),
            sortable: true,
            value: "city",
            width: '25%',
        },
        {
            text: this.$vuetify.lang.t("$vuetify.contacts_count"),
            sortable: true,
            value: "associated_contacts",
            sortText: this.$vuetify.lang.t("$vuetify.contacts_count_total"),
            width: '10%',
            align: 'center',
        },
        {
            text: this.$vuetify.lang.t("$vuetify.positions_count"),
            sortable: true,
            value: "associated_positions",
            sortText: this.$vuetify.lang.t("$vuetify.positions_count_total"),
            width: '10%',
            align: 'center',
        },
      ],
      filters: [
        {label: 'searches', type: 'text-field'},
        {label: 'state', type: 'select', multiple: true, items: [
          {text: this.$vuetify.lang.t("$vuetify.suspect"), value: "suspect"},
          {text: this.$vuetify.lang.t("$vuetify.prospect"), value: "prospect"},
          {text: this.$vuetify.lang.t("$vuetify.active_client"), value: "active_client"},
          {text: this.$vuetify.lang.t("$vuetify.inactive_client"), value: "inactive_client"},
        ]},
        {label: 'name', type: 'text-field'},
        {label: 'postal_city_code', type: 'city-list'},
        {label: 'associated_contacts', type: 'toggle', items: [
          {text: this.$vuetify.lang.t("$vuetify.yes"), value: true},
          {text: this.$vuetify.lang.t("$vuetify.no"), value: false}
        ] },
        {label: "contact_names", type: 'text-field'},
        {label: "id", type: 'text-field', text: this.$vuetify.lang.t("$vuetify.reference")},
        {label: 'associated_positions', type: 'toggle', items: [
          {text: this.$vuetify.lang.t("$vuetify.yes"), value: true},
          {text: this.$vuetify.lang.t("$vuetify.no"), value: false}
        ]},
      ],
      selected: [] as string[],
      translation: 'companies',
      headersSplitPane: [] as Array<any>,
      url: "" as string,
      loadingUrl: true
    }
  },
  computed: {
    stateAgency: {
      get() {
        return state.agency
      },
      cache: false  // Need to force the reactivity
    }
  },
  watch: {
    stateAgency: {
      async handler() {
        await this.reloadCompanyList()
      },
      deep: true,
      immediate: false
    }
  },
  async mounted() {
    this.loadingUrl = true
    try {
      this.url = await this.buildUrl()
    } finally {
      this.loadingUrl = false
    }

    (EventBus as any).$on('companyListReload', () => {
      (this.$refs.list as any).getData()
    })
  },
  methods: {
    async reloadCompanyList() {
      this.loadingUrl = true
      try {
        this.url = await this.buildUrl()
        if (this.$refs.list) {
          (this.$refs.list as any).getData()
        }
      } finally {
        this.loadingUrl = false
      }
    },
    async buildUrl(): Promise<string> {
      let spaceIdParameter = ""
      if (state.agency) {
        spaceIdParameter = state.agency
      } else {
        const spaces = await this.getSpacesForThisConnectedUser()
        spaceIdParameter = spaces.join(',')
      }
      return `/v1/company?deleted_at=${this.deleted}&space_id=${spaceIdParameter}&fields=id,name,postal_code,city,associated_contacts,associated_positions,state,siret,siren,complete_name,address,phone,website,manager_name,category,legal_form,ape_code,activity_details,code_idcc,code_idcc_label,annual_revenue_k,employee_count,etp_count,external_ref,is_foreign,is_group,projects,flexibility_management,comment,created_at,updated_at,deleted_at,client_id`
    },
    async getSpacesForThisConnectedUser(): Promise<number[]> {
      return await axios
        .get(`/v1/spaces?deleted=false`)
        .then((spacesResponse: AxiosResponse) => {
          return spacesResponse.data.map((space: Space) => space.id)
        })
        .catch((error: AxiosError) => {
          EventBus.$emit('snackbar',{axiosError: error.response})
        })
    },
    processAction(ids: string[], action: 'archive' | 'reactivate') {
      this.action = action;
      this.selected = ids;
      this.confirmationDialog = true;
    },
    async submitAction() {
      this.loading = true;
      try {
        if (this.action === 'archive') {
          await this.deleteSelection(this.selected);
        } else {
          await this.restoreSelection(this.selected);
        }
      } finally {
        this.confirmationDialog = false;
        (EventBus as any).$emit('companyListReload')
        this.loading = false;
        (this.$refs.list as any).getData()
        this.selected = []
      }
    },
    async deleteSelection(ids: string[]) {
      let snackbar: SnackBarData = {message: this.$vuetify.lang.t('$vuetify.no_selection'), color: "error"}
      if (ids.length > 0) {
        await axios.delete(`/v1/company`, {
          data: ids
        })
        .then(() => {
          snackbar = {message: this.$vuetify.lang.t('$vuetify.archive_successful'), color: "success"}
          this.$emit('updateData');
        })
        .catch((error: AxiosError) => {
          snackbar = {axiosError: error}
        })
      }
      (EventBus as any).$emit('snackbar', snackbar as unknown as SnackBarData)
    },
    async restoreSelection(ids: string[]) {
      let snackbar: SnackBarData = {message: this.$vuetify.lang.t('$vuetify.no_selection'), color: "error"}
      if (ids.length > 0) {
        await axios.put(`/v1/company/reactivate`, ids)
        .then(() => {
          snackbar = {message: this.$vuetify.lang.t('$vuetify.restoration_successful'), color: "success"}
          this.$emit('updateData');
        })
        .catch((error: AxiosError) => {
          snackbar = {axiosError: error}
        })
      }
      (EventBus as any).$emit('snackbar', snackbar as unknown as SnackBarData)
    },
    redirectToCompany(id: number) {
      this.$router.push(`/company/update/${id}`)
    }
  },
  beforeDestroy() {
    (EventBus as any).$off('companyListReload')
  }
})
